import BaseService from './BaseService';
import Config from "./Config";
import AppService from './AppService';
import UrlService from './UrlService';
import UrlParamsService from './UrlParamsService';
import Oauth2Service from "./Oauth2Service";
import RestService from "./RestService";
import RefreshTimer from "../data/RefreshTimer";
import AuthStorage from "../storage/AuthStorage";
import { once, withTimeout } from "../utils/promise";
import NetworkService from "./NetworkService";
import AppLifecycleService from "./AppLifecycleService";
import LogManager from "./LogManager";
import i18n from "i18next";
import type UserData from "../data/UserData";
import type { URLOpenListenerEvent } from '@capacitor/app';

/**
 * Wrapper class to manage use account and authentication.
 *
 * This API should keep stable whatever the auth method used.
 */
class SiteAuth extends BaseService {

  /**
   * Keeps track of received valid user.
   */
  static validUser = false;

  /**
   * Whether to retry authentication if it fails.
   */
  static retryAuth = true;

  /**
   * Pending client authentication.
   */
  static clientAuthPromise: Promise<void> | undefined;

  /**
   * this function sends the app to map view
   */
  static authenticateHandle: () => void;

  /**
   * Stored user data.
   */
  static userData: UserData | null;

  static refreshTimer = new RefreshTimer();

  /**
   * Storage key for the URL params saved across the login redirect.
   */
  static PENDING_PARAMS_KEY = 'login_pending_params';

  /**
   * Check whether this needs refresh.
   *
   */
  static needsRefresh(): boolean {
    return this.refreshTimer.needsRefresh() && this.isOnline();
  }

  /**
   * True if log in not required or we've got a valid user, not expired.
   */
  static isValidUser(): boolean {
    return !Config.isLoginRequired() || this.isUserRegistered() && !this.needsRefresh();
  }

  /**
   * True if current user is registered.
   */
  static isUserRegistered(): boolean {
    return !!(this.validUser && this.userData);
  }

  /**
   * True if current user is anonymous.
   */
  static isUserAnonymous(): boolean {
    return !this.isUserRegistered();
  }

  /**
   * True if user is anonymous and can log in.
   */
  static canLogin(): boolean {
    return Config.isLoginAllowed() && this.isUserAnonymous();
  }

  /**
   * True if user is registerd and can log out.
   */
  static canLogout(): boolean {
    return Config.isLoginAllowed() && this.isUserRegistered();
  }

  /**
   * True if user is anonymous and can register.
   */
  static canRegister(): boolean {
    return Config.isLoginAllowed() && this.isUserAnonymous();
  }

  /**
   * Check authenticated state.
   *
   * @return True if authenticated with OAuth or cookies enabled.
   */
  static async checkAuthentication(): Promise<boolean> {
    this.debug('checkAuthentication');

    // Load user from storage if possible
    if (this.isValidUser()) {
      return true;
    } else if (Oauth2Service.hasAccessToken(false)) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Check whether the app is authorized to run without prompting for login.
   *
   * Only restricts the PWA: the native app is always authorized. On the PWA
   * it is authorized when authenticated or when login is not required
   * (app_login_required = false).
   */
  static async checkPwaAuthentication(): Promise<boolean> {
    if (!AppService.isPwa()) {
      return true;
    }
    return this.checkAuthentication();
  }

  /**
   * Initializes authentication using implicit flow.
   *
   * To be called any time on pwa after we get a 401 response, thus:
   * - No valid session.
   *
   * @return
   * - True if authentication is complete
   * - False if auth failed or needs more steps
   */
  static async initAuthentication(with_registration = false): Promise<boolean> {
    this.debug('initAuthentication implicit');

    // The OAuth round trip comes back to the bare callback URL, losing the
    // query params of the deep link that opened the app: save them here and
    // restore them on the post-login startup.
    // @see init
    this.savePendingUrlParams();

    await Oauth2Service.requestAuthorization(with_registration);

    // Needs further processing / redirects.
    this.retryAuth = false;
    return false;
  }

  /**
   * Save the current URL params so they survive the login redirect.
   *
   * Saving an empty set clears any previous leftover.
   */
  static savePendingUrlParams(): void {
    const params = UrlParamsService.toQueryString();
    if (params) {
      AuthStorage.setItem(this.PENDING_PARAMS_KEY, params);
    } else {
      AuthStorage.removeItem(this.PENDING_PARAMS_KEY);
    }
  }

  /**
   * Restore and delete the URL params saved before the login redirect.
   *
   * If the current URL carries its own params they win: a new deep link is
   * fresher than the one that started the login.
   */
  static restorePendingUrlParams(): void {
    const pending = AuthStorage.getItem(this.PENDING_PARAMS_KEY);
    if (pending) {
      AuthStorage.removeItem(this.PENDING_PARAMS_KEY);
      if (!UrlParamsService.toQueryString()) {
        this.debug('Restoring URL params saved across login', pending);
        UrlParamsService.setParamsFromQueryString(pending);
      }
    }
  }

  /**
   * Reset authentication, delete all.
   *
   * @return
   * - True if authentication is complete
   * - False if auth failed or needs more steps
   */
  static async resetAuthentication(): Promise<void> {
    this.debug('resetAuthentication');
    this.validUser = false;
    this.removeUserData();
    Oauth2Service.reset();
  }

  /**
   * Restart authentication from scratch.
   *
   * To be called any time after we get a 401 response, thus:
   * - No valid session.
   * - No valid oauth tokens.
   *
   * @return
   * - True if authentication is complete
   * - False if auth failed or needs more steps
   */
  static async restartAuthentication(): Promise<boolean> {
    this.resetAuthentication();
    return this.initAuthentication();
  }

  /**
   * Save the user data to storage.
   *
   * @param userData User data received from REST service
   */
  static setUserData(userData: UserData): UserData {
    this.debug("Set user data", userData);
    this.userData = userData;
    this.validUser = true;
    this.refreshTimer.setRefreshLast();
    return userData;
  }

  /**
   * Get stored userData from variable.
   */
  static async getUserData(): Promise<UserData | null> {
    // if (this.isValidUser()) {
      return this.userData;
    // }
  }

  /**
   * Remove user data, clean variables and storage.
   */
  static removeUserData(): void {
    this.validUser = false;
    this.userData = null;
  }

  /**
   * Re-check authentication periodically.
   */
  static async refreshAuthentication(): Promise<boolean> {
    if (this.isValidUser()) {
      return true;
    }
    else {
      this.debug("Refresh / Recheck user");
      this.validUser = false;
      this.retryAuth = true;
      return this.checkAuthentication()
        .then(async auth =>  { return !!(auth && await this.reloadUserData())});
    }
  }

  /**
   * Initializes page requiring a valid user.
   */
  static async requireValidUser(): Promise<boolean> {
    if (this.isValidUser()) {
      this.debug('requireValidUser OK');
      return true;
    }
    else {
      this.debug('requireValidUser RELOAD');
      try{
        const result = await this.reloadUserData();
        return !!result;
      } catch {
        return false;
      }

    }
  }

  /**
   * Reload user data from server.
   *
   * @param signal Para abortar la petición.
   */
  static async reloadUserData(signal?: AbortSignal): Promise<UserData | null> {
    return new Promise<UserData | null>((resolve)=> {
      RestService.getCurrentUser(signal)
          .then(async (user) => {
            resolve(this.setUserData(user));
          })
          .catch(error => {
            this.validUser = false;
            this.logger().warn('Error loading user data', error);
            resolve(null);
          });
    })
  }

  /**
   * Process oauth2 authentication
   *
   * Parameters:
   * - usarname
   * - password
   *
   * @param username
   * @param password
   */
  static async authenticate(username: string, password: string): Promise<void> {
    return Oauth2Service.requestAccessToken(Oauth2Service.PASSWORD_REQUEST_TYPE, username, password)
      .then(() => this.reloadUserData())
      .then(() => this.authenticateHandle())
      .catch((error) => {
        this.debug("Site Authentication failed", error);
        if (error.type === 'user') {
          return Promise.reject(error);
        }
      });
  }

  /**
   * Process oauth2 authentication
   *
   * Deduplicated: a deep link arriving while the startup authentication is
   * still in flight joins it instead of requesting a second token.
   */
  static async clientAuthenticate(): Promise<void> {
    return once(this, 'clientAuthPromise', () => this.runClientAuthentication());
  }

  /**
   * Requests the client credentials token and loads the user data.
   *
   * @see clientAuthenticate
   */
  static async runClientAuthentication(): Promise<void> {
    const controller = new AbortController();
    let attempt: Promise<unknown> = Oauth2Service.requestAccessToken(
      Oauth2Service.CLIENT_CREDENTIALS_REQUEST_TYPE, null, null, null, controller.signal,
    ).then(() => this.reloadUserData(controller.signal));
    // Ni axios ni las peticiones de la app tienen timeout: una petición que se
    // queda colgada al perder cobertura no llegaría nunca al catch, y con ella
    // ni el reintento ni las llamadas siguientes, que compartirían esta misma
    // (#673). Pasado el plazo se da por fallida y se abortan sus peticiones:
    // si no, su respuesta podría llegar después de que otro intento consiga
    // el token, y un token viejo o un 401 lo pisarían o lo borrarían.
    const seconds = Config.config().oauth2_token_timeout_seconds;
    if (seconds) {
      attempt = withTimeout(
        attempt,
        seconds * 1000,
        `Sin respuesta del token anónimo en ${seconds} s`,
        () => controller.abort(),
      );
    }

    return attempt
        .then(() => {
          // reloadUserData no falla nunca: devuelve null. Si /current_user
          // responde un 401, además, el token se borra, y sin token no hay
          // nada que dar por bueno. Los datos de usuario no se exigen: sin
          // login no se usan, y en develop su fallo tampoco impedía el mapa.
          if (!Oauth2Service.hasAccessToken(false)) {
            throw new Error('Sin token tras la autenticación anónima');
          }
          this.stopClientAuthRetry();
          this.authenticateHandle();
        })
        .catch((error) => {
          this.logger().warn("Site Client Authentication failed", error);
          if (error.type === 'user') {
            return Promise.reject(error);
          }
          // Sin login, la app no puede hacer nada sin este token: en vez de
          // quedarse sin él hasta que se reinicie, se vuelve a pedir (#673).
          if (!AppService.needsUserLogin()) {
            this.scheduleClientAuthRetry();
          }
        });
  }

  /**
   * Reintento del token anónimo en curso. @see scheduleClientAuthRetry
   */
  static clientAuthRetry: {
    /** Intentos fallidos desde el último que salió bien. */
    attempts: number;
    /** Intento programado, si hay uno pendiente. */
    timer?: ReturnType<typeof setTimeout>;
    /** Motivo por el que el intento está pospuesto, si lo está. */
    waitingFor?: string;
  } = { attempts: 0 };

  /**
   * Programa otro intento del token anónimo, con una espera que se dobla en
   * cada intento: las mismas que la recarga del mapa, de
   * map_reload_delay_seconds a map_reload_max_delay_seconds.
   *
   * Avisa al usuario en cada fallo, porque sin el token no hay mapa.
   */
  static scheduleClientAuthRetry(): void {
    const retry = this.clientAuthRetry;
    if (retry.timer) {
      return;
    }

    const config = Config.config();
    const seconds = Math.min(
      config.map_reload_delay_seconds * 2 ** retry.attempts,
      config.map_reload_max_delay_seconds,
    );
    this.logger().info(`Token anónimo: nuevo intento en ${seconds} s`);
    LogManager.createError(i18n.t('offline.retrying'));
    this.armClientAuthRetry(seconds * 1000);
  }

  /**
   * Arma el temporizador del intento. Sin red fallaría seguro, y en segundo
   * plano nadie ve la app: se pospone con la misma espera, sin contar como
   * intento.
   */
  static armClientAuthRetry(delay: number): void {
    const retry = this.clientAuthRetry;
    retry.timer = setTimeout(() => {
      retry.timer = undefined;
      const reason = NetworkService.isOffline()
        ? 'hasta que vuelva la red'
        : AppLifecycleService.isInBackground()
          ? 'mientras la app está en segundo plano'
          : undefined;
      if (reason) {
        if (retry.waitingFor !== reason) {
          retry.waitingFor = reason;
          this.logger().info(`Token anónimo: intento pospuesto ${reason}`);
        }
        this.armClientAuthRetry(delay);
        return;
      }

      retry.waitingFor = undefined;
      retry.attempts++;
      this.logger().info(`Token anónimo: intento ${retry.attempts}`);
      this.clientAuthenticate();
    }, delay);
  }

  /**
   * Cancela el reintento pendiente: ya hay token, por este intento o por otro
   * que llegó antes, como el de un deep link.
   */
  static stopClientAuthRetry(): void {
    clearTimeout(this.clientAuthRetry.timer);
    this.clientAuthRetry = { attempts: 0 };
  }

  /**
   * Makes sure there is authorization data before acting on a deep link.
   *
   * A deep link can reach the app while the startup client authentication is
   * still in flight, or even before it starts. Everything requested from there
   * (the user data, the URL feature card) would then run without a token and
   * fail silently, so we wait for it: this joins the pending authentication, or
   * starts one when the app does not require a user login.
   *
   * @return Whether we ended up with authorization data.
   */
  static async ensureAuthentication(): Promise<boolean> {
    if (Oauth2Service.hasAccessToken(false)) {
      return true;
    }

    if (!AppService.needsUserLogin()) {
      await this.clientAuthenticate().catch((error) => {
        this.debug("Client authentication for URL event failed", error);
      });
    }

    return Oauth2Service.hasAccessToken(false);
  }

  /**
   * Handle URL parameters if present.
   *
   * @param url
   *   Raw URL from window.location.href or event.url
   *
   * @return True if URL has been handled successfully.
   */
  static async handleUrl(url: string): Promise<boolean> {
    const urlObject = new URL(url);
    return Oauth2Service.extractAuthCode(urlObject.searchParams);
  }

  /**
   * Handle URL event from UrlListener
   */
  static async handleUrlEvent(event: URLOpenListenerEvent): Promise<void> {
    this.debug("Handling URL event", event);

    // Do not act on the link until there is authorization data: on a cold start
    // opened from a deep link this runs while the startup authentication is
    // still in flight, and reloadUserData would fail (and resolve anyway,
    // sending the app to the map) with every request unauthorized.
    await this.ensureAuthentication();

    return this.handleUrl(event.url)
      .then(() => this.reloadUserData())
      .then(() => this.authenticateHandle())
      .catch((error) => {
         this.debug("Authentication error", error);
      });
  }

  /**
   * Initialize, check URL auth callback, etc..
   *
   * If successful authorization, reloads pwa (redirect)
   *
   * @return True unless we need to stop flow and redirect.
   */
  static async init(): Promise<boolean> {
    this.logger().debug('SiteAuth.init()');

    // Initialize refresh timer.
    this.refreshTimer.setRefreshTime(Config.getRefreshTime());

    const result = await this.handleUrl(window.location.href)
      .then((result) => {
         if (result) {
           UrlService.redirectBaseUrl();
           return false;
         }
         else {
           return true;
         }
       })
      .catch((error) => {
         this.debug("Authentication error", error);
         return true; // Continue so we can retry.
      });

    // Normal startup (not redirecting after a code exchange): restore the
    // URL params of the deep link that started a login, if any.
    if (result) {
      this.restorePendingUrlParams();
    }

    Oauth2Service.init();

    return result;
  }

}

export default SiteAuth;
